import { useState } from "react";
import { useAsync } from "react-use";
import { Profile, useAuth } from "../../shared/AuthContext";
import { getUiConfig } from "../../shared/api";
import { Stack, Text, Checkbox } from "@mantine/core";

function ReadonlySection({ title, items }: { title: string; items: string[] }) {
  return (
    <div>
      <Text fw={600}>{title}</Text>
      {items.length > 0 ? (
        <Stack gap={2} mt={4}>
          {items.map((it, i) => <Text key={i} c="dimmed">{it}</Text>)}
        </Stack>
      ) : (
        <Text c="dimmed" mt={4}>—</Text>
      )}
    </div>
  );
}

// Roles a dev can turn off here to test as a lower-privileged user (see
// backend allow_role_override), without a second account. Listed fixed and
// independent of the "Роли" read-out above: once a role is disabled it drops
// out of profile.roles, so deriving the checkboxes from that list would make
// them vanish with no way back.
const TOGGLEABLE_ROLES = ["admin", "reviewer", "supervisor"];

/** Read-only application roles and groups — plain, one per line, for the
 *  right column. When the backend allows testing as a lower-privileged user,
 *  a "Тест ролей" toggle section appears right below the roles it affects. */
export default function RolesGroups({ profile }: { profile: Profile }) {
  const auth = useAuth();
  const { value: config } = useAsync(getUiConfig, []);
  const [disabledRoles, setDisabledRolesState] = useState(() => auth.getDisabledRoles());

  function toggleRole(role: string, keep: boolean) {
    const next = keep ? disabledRoles.filter(r => r !== role) : [...disabledRoles, role];
    setDisabledRolesState(next);
    auth.setDisabledRoles(next);
    // Every role-gated hook/fetch reads roles fixed at load time — reload so
    // the whole app (including the "Роли" list above) reflects the new set.
    window.location.reload();
  }

  return (
    <Stack gap="md">
      <ReadonlySection title="Роли" items={profile.roles} />
      {config?.allow_role_override && (
        <div>
          <Text fw={600}>Тест ролей (dev)</Text>
          <Stack gap={4} mt={8}>
            {TOGGLEABLE_ROLES.map(role => (
              <Checkbox
                key={role}
                size="md"
                c="dimmed"
                variant="outline"
                label={role}
                checked={!disabledRoles.includes(role)}
                onChange={e => toggleRole(role, e.currentTarget.checked)}
              />
            ))}
          </Stack>
        </div>
      )}
      <ReadonlySection title="Группы" items={profile.groups} />
    </Stack>
  );
}
